import { useEffect, useState } from "react";

import { FiltersSideDrawer } from "@vega-core/components/Modals/FiltersModal/FiltersSideDrawer";

import { allFilterDefaultValues } from "../hooks/useTableFilters";
import {
  type FilterConfig,
  type FilterState,
  FilterType,
  type FilterValue,
  type FilterValuesByType,
  type TableFilterId,
} from "../types";

import { RenderFilter } from "./RenderFilter";

type Props = {
  disabled?: boolean;
  originalFilters: readonly FilterConfig[];
  filterState: FilterState;
  isLoading: boolean;
  onFilterChange: (filterId: string, value: FilterValue) => void;
  onResetFilters: () => void;
  tableId: TableFilterId;
};

export function FiltersOverflowSideDrawer({
  originalFilters,
  filterState,
  isLoading,
  onFilterChange,
  onResetFilters,
  tableId,
  disabled = false,
}: Props) {
  // Maintain a local draft of filter state while modal is open
  const [draftState, setDraftState] = useState<FilterState>(filterState);

  useEffect(() => {
    setDraftState(filterState);
  }, [filterState]);

  const handleTempChange = (id: string, value: FilterValue) => {
    const type = originalFilters.find((f) => f.id === id)?.type;

    if (!type) {
      return;
    }

    setDraftState((prev) => ({
      ...prev,
      [id]: {
        value: value as FilterValuesByType[FilterType],
        type,
      },
    }));
  };

  const commitChanges = () => {
    for (const f of originalFilters.filter((filter) => !filter.hideFromModal)) {
      const id = f.id;
      const nextVal = draftState[id].value;
      const prevVal = filterState[id].value;

      if (JSON.stringify(prevVal) !== JSON.stringify(nextVal)) {
        onFilterChange(id, nextVal as FilterValue);
      }
    }
  };

  // Only consider filters that are not hidden from modal for disabled state
  // Also filter out filters that don't exist in draftState yet (handles dynamic filter additions)
  const visibleFilters = originalFilters.filter(
    (filter, index) =>
      !filter.hideFromModal &&
      !(index === 0 && filter.type === FilterType.Search) &&
      filter.id in draftState,
  );

  const visibleDraftState = Object.fromEntries(
    visibleFilters.map((filter) => [filter.id, draftState[filter.id]]),
  );
  const visibleFilterState = Object.fromEntries(
    visibleFilters.map((filter) => [filter.id, filterState[filter.id]]),
  );
  const isDisabled =
    JSON.stringify(visibleDraftState) === JSON.stringify(visibleFilterState);

  const filterCount = originalFilters
    .filter((filter) => !filter.hideFromModal)
    .filter((filter) => {
      const filterValue = filterState[filter.id];

      return !allFilterDefaultValues.includes(
        JSON.stringify(filterValue.value),
      );
    }).length;

  return (
    <FiltersSideDrawer
      actionPanelActions={{
        primaryActionProps: {
          disabled: isDisabled,
        },
        onPrimaryAction: commitChanges,
        onSecondaryAction: () => onResetFilters(),
      }}
      disabled={disabled}
      filterCount={filterCount}
      isLoading={isLoading}
      onClose={() => {
        setDraftState(filterState);
      }}
    >
      <div className="flex flex-col gap-8">
        {visibleFilters.map((filter) => (
          <RenderFilter
            disabled={disabled}
            filter={filter}
            filterState={draftState}
            isLoading={isLoading}
            key={filter.id}
            onFilterChange={handleTempChange}
            showLabel
            tableId={tableId}
            type={filter.type}
          />
        ))}
      </div>
    </FiltersSideDrawer>
  );
}
